import { ProcessedSearchResults } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import {
  AnalyticsComponentName,
  parseCarouselItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '@app/pages/i/analytics';
import {
  Box,
  constants,
  Skeleton,
  Stack,
  Text,
  TextLink,
} from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import {
  defineMessages,
  FormattedMessage,
  type MessageDescriptor,
  useIntl,
} from '@redbubble/next/intl';
import { AppProps, DeviceType } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import { CategorySearchResultsPageProps } from 'pages/shop/types';
import { useMemo } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  type CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import {
  mapSearchResultMediaCardProps,
  mapSearchResultPriceProps,
} from '../SuggestedProductsCarousel/utils';
import styles from './L2Carousel.module.scss';

const messages = defineMessages({
  seeAll: { defaultMessage: 'See all' },
  error: { defaultMessage: 'Unable to load products' },
});

const SKELETON_COUNT = 6;

const LARGE_DESKTOP_MIN_WIDTH = 1280;
const DESKTOP_ITEMS_IN_VIEW = 4;
const LARGE_DESKTOP_ITEMS_IN_VIEW = 6;

export interface L2CarouselProps {
  title: MessageDescriptor;
  results: ProcessedSearchResults;
  seeAllPath: string;
  slug: string;
  isLoading?: boolean;
  hasError?: boolean;
}

export const L2Carousel = ({
  title,
  results,
  seeAllPath,
  slug,
  isLoading = false,
  hasError = false,
}: L2CarouselProps) => {
  const intl = useIntl();
  const { listsInclusions, deviceType } = usePageProps<
    AppProps & CategorySearchResultsPageProps
  >();
  const { viewportWidth, isOrAbove } = useViewportWidth(deviceType);

  const desktopItemsInView =
    (viewportWidth ?? 0) >= LARGE_DESKTOP_MIN_WIDTH
      ? LARGE_DESKTOP_ITEMS_IN_VIEW
      : DESKTOP_ITEMS_IN_VIEW;

  const isDesktop = isOrAbove(DeviceType.Desktop);

  const seeAllLink = (
    <TextLink intent={constants.PRIMARY} href={seeAllPath}>
      {intl.formatMessage(messages.seeAll)}
    </TextLink>
  );

  const contextValue: CarouselSectionContextValue = useMemo(
    () => ({
      mediaCardPropsMapper: mapSearchResultMediaCardProps(listsInclusions),
      pricePropsMapper: mapSearchResultPriceProps,
      analytics: {
        componentName: AnalyticsComponentName.L2CategoryCarousel,
        listID: AnalyticsItemsList.CategoryPageCarousel,
        searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
        inViewTracking: () => {
          const analyticsItems = results
            .map((item, index) =>
              parseCarouselItemToAnalyticsEcommerceItem({ item, index })
            )
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListEvent({
            items: analyticsItems,
            listID: AnalyticsItemsList.CategoryPageCarousel,
            topic: slug,
            searchUUIDStorageKey: SearchUUIDStorageKey.Latest,
          });
        },
      },
      rsLocation: RSProductClickedLocation.SuggestedProductsCarousel,
      rsPageType: RSPageType.Search,
    }),
    [listsInclusions, results, slug]
  );

  if (isLoading) {
    return (
      <Stack spacing="m" data-testid={`l2-carousel-loading-${slug}`}>
        <Skeleton height="28px" width="200px" animated />
        <div className={styles.skeletonRow}>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders are static
            <div key={i} className={styles.skeletonCard}>
              <Skeleton ratio={constants.RATIO_PORTRAIT} animated />
            </div>
          ))}
        </div>
      </Stack>
    );
  }

  if (hasError) {
    return (
      <Stack spacing="m" data-testid={`l2-carousel-error-${slug}`}>
        <Text type="display3">
          <FormattedMessage {...title} />
        </Text>
        <Text element="p" muted>
          {intl.formatMessage(messages.error)}
        </Text>
      </Stack>
    );
  }

  if (!results.length) return null;

  return (
    <Box data-testid={`l2-carousel-${slug}`}>
      <CarouselSectionContext.Provider value={contextValue}>
        <CarouselSection.Container>
          <div className={styles.header}>
            <Text type="display3">
              <FormattedMessage {...title} />
            </Text>
            {isDesktop && seeAllLink}
          </div>
          <CarouselSection.Carousel
            itemsInView={{ mobile: 2, tablet: 4, desktop: desktopItemsInView }}
          >
            {results.map((product, index) => (
              <CarouselSection.CarouselSlide
                key={`l2-${slug}-${product.inventoryItem?.id ?? index}`}
              >
                <CarouselSection.CarouselMediaCard
                  data={product}
                  index={index}
                />
              </CarouselSection.CarouselSlide>
            ))}
          </CarouselSection.Carousel>
          {!isDesktop && (
            <div className={styles.seeAllMobile}>{seeAllLink}</div>
          )}
        </CarouselSection.Container>
      </CarouselSectionContext.Provider>
    </Box>
  );
};
